import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { trackOpenSupport } from '../../config/analytics';
import {
  displayIntercomMessenger,
  isIntercomAvailable,
  useInitializeIntercom,
} from '../../config/intercom';
import { useSupportFailedModal } from './support-failed-modal';

export interface SupportOpenOptions {
  source: SupportOpenSource;
  beforeOpen?: () => Promise<void>;
}

export interface SupportContextValue {
  open: (options: SupportOpenOptions) => Promise<void>;
}

export interface SupportContextProviderProps {
  children: ReactNode;
}

enum SupportOpenSource {
  CHAT = 'chat',
  MENU = 'menu',
  DIRECT_CONNECT = 'direct-connect',
}

const SupportContext = createContext<SupportContextValue | undefined>(
  undefined
);

const IntercomSupportContextProvider = (props: SupportContextProviderProps) => {
  const { children } = props;

  useInitializeIntercom();

  const { modal: intercomFailedModal, openModal: openIntercomFailedModal } =
    useSupportFailedModal();

  const openSupport = useCallback(
    async (options: SupportOpenOptions) => {
      const { source, beforeOpen } = options;

      const available = await isIntercomAvailable();

      if (available) {
        if (typeof beforeOpen === 'function') {
          await beforeOpen();
        }

        displayIntercomMessenger();
      } else {
        openIntercomFailedModal({});
      }

      trackOpenSupport(source);
    },
    [openIntercomFailedModal]
  );

  const contextValue = useMemo<SupportContextValue>(() => {
    return {
      open: openSupport,
    };
  }, [openSupport]);

  return (
    <SupportContext.Provider value={contextValue}>
      {children}
      {intercomFailedModal}
    </SupportContext.Provider>
  );
};

const useSupportContext = (): SupportContextValue => {
  return useContext(SupportContext) as SupportContextValue;
};

export {
  IntercomSupportContextProvider,
  useSupportContext,
  SupportContext,
  SupportOpenSource,
};
